export enum TrainingClassStatus {
  NeedsAction = 0,
  Unavailable = 1,
  Scheduled = 2,
  Completed = 3,
  Cancelled = 4,
}

export enum UnavailableReasonCode {
  NoSessions = 0,
  NoUpcomingSessions = 1,
  AllSessionsFull = 2,
  NoSessionsBeforeWorkSchedule = 3,
}

export function getVoidReasonText(voidReason?: number): string {
  switch (voidReason) {
    case 1:
      return 'Schedule declined';
    case 2:
      return 'Schedule removed';
    case 3:
      return 'Removed from election';
    case 4:
      return 'Role requirement removed';
    case 5:
      return 'Removed by admin';
    case 6:
      return 'Location removed';
    default:
      return 'No longer required';
  }
}

export interface TrainingDueDateMessage {
  text: string;
  isPastDue: boolean;
}

const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

function startOfDay(value: Date) {
  return new Date(value.getFullYear(), value.getMonth(), value.getDate()).getTime();
}

export function getTrainingDueDateMessage({
  dueDate,
  now = new Date(),
  formatDueDate,
}: {
  dueDate?: Date | null;
  now?: Date;
  formatDueDate: (value: Date) => string;
}): TrainingDueDateMessage | null {
  if (!dueDate || Number.isNaN(dueDate.getTime())) return null;

  const daysUntilDue = Math.round((startOfDay(dueDate) - startOfDay(now)) / MILLISECONDS_PER_DAY);

  if (daysUntilDue < 0) return { text: `Past due - was due ${formatDueDate(dueDate)}`, isPastDue: true };
  if (daysUntilDue === 0) return { text: 'Due today', isPastDue: false };
  if (daysUntilDue === 1) return { text: 'Due tomorrow', isPastDue: false };

  return { text: `Due ${formatDueDate(dueDate)}`, isPastDue: false };
}

export function getTrainingStatusMessage({
  status,
  attendanceNotRecorded = false,
  sessionDate,
  availableSessionCount = 0,
  unavailableReason,
  voidReason,
  formatSessionDate,
}: {
  status: TrainingClassStatus;
  attendanceNotRecorded?: boolean;
  sessionDate?: string;
  availableSessionCount?: number;
  unavailableReason?: UnavailableReasonCode | number;
  voidReason?: number;
  formatSessionDate?: (value: string) => string;
}): string | null {
  switch (status) {
    case TrainingClassStatus.Completed:
      if (attendanceNotRecorded) return 'Session completed';
      return 'Training completed';

    case TrainingClassStatus.Scheduled:
      if (sessionDate && formatSessionDate) {
        return `Scheduled for ${formatSessionDate(sessionDate)}`;
      }
      return 'Scheduled for a session';

    case TrainingClassStatus.NeedsAction:
      return availableSessionCount === 1 ? '1 session available' : `${availableSessionCount} sessions available`;

    case TrainingClassStatus.Unavailable:
      switch (unavailableReason) {
        case UnavailableReasonCode.NoSessions:
          return 'No sessions available yet';
        case UnavailableReasonCode.NoUpcomingSessions:
          return 'No upcoming sessions available';
        case UnavailableReasonCode.AllSessionsFull:
          return 'All sessions are full';
        case UnavailableReasonCode.NoSessionsBeforeWorkSchedule:
          return 'No sessions available before your work schedule';
        default:
          return null;
      }

    case TrainingClassStatus.Cancelled:
      return getVoidReasonText(voidReason);

    default:
      return null;
  }
}
